import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
    getServerDetailsAPI, 
    getAdminsForServerAPI, 
    getEmployeesForServerAPI,
    getServiceUsersForServerAPI,
    updateServerAPI
} from '../services/serverService';
import DisableServerModal from '../components/DisableServerModal';
import EditServerModal from '../components/EditServerModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

const ServerDetailsPage = () => {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userRole } = useAuth();
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');
    
    // Data State
    const [server, setServer] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [serviceUsers, setServiceUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Modal States
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const fetchAllDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            const [serverResponse, adminResponse, employeeResponse, serviceUserResponse] = await Promise.all([
                getServerDetailsAPI(serverId),
                getAdminsForServerAPI(serverId),
                getEmployeesForServerAPI(serverId),
                getServiceUsersForServerAPI(serverId)
            ]);
            setServer({ id: serverId, ...serverResponse.data.data });
            setAdmins(adminResponse.data.data);
            setEmployees(employeeResponse.data.data);
            // setServiceUsers(serviceUserResponse.data.data);
            const responseData = serviceUserResponse.data?.data;
            const extractedUsers = Array.isArray(responseData) 
                ? responseData 
                : (responseData?.users || []);

            setServiceUsers(extractedUsers);
        } catch (err) {
            setError('Failed to load server details.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [serverId]);

    useEffect(() => {
        if (serverId) fetchAllDetails();
    }, [serverId, fetchAllDetails]);

    // --- STAFF LIMIT LOGIC ---
    const isUserActive = (u) => u.active !== undefined ? u.active === true : u.status === true;

    const activeAdminsCount = admins.filter(isUserActive).length;
    const activeEmployeesCount = employees.filter(isUserActive).length;
    const activeServiceUsersCount = serviceUsers.filter(isUserActive).length;
    const totalActiveStaff = activeAdminsCount + activeEmployeesCount;
    
    const maxUsers = server?.maxUsers || 0;
    const isGlobalOverLimit = maxUsers > 0 && totalActiveStaff > maxUsers;

    const handleAddShortcut = (path) => {
        navigate(path, { state: { openAddModal: true, currentCount: totalActiveStaff, maxLimit: maxUsers } });
    };

    const handleConfirmToggle = async (reason) => {
        if (!server) return;
        const isCurrentlySuspended = server.status === 'suspended';
        const newStatus = isCurrentlySuspended ? 'active' : 'suspended';
        
        try {
            await updateServerAPI(serverId, { status: newStatus, reason: reason });
            alert(`Server status updated successfully.`);
            setDisableModalOpen(false);
            fetchAllDetails();
        } catch (error) {
            console.error("Failed to update server status", error);
            alert("Error updating status: " + (error.response?.data?.message || error.message));
        }
    };

    if (isLoading) return <div className="page-container"><h2>Loading Server Details...</h2></div>;
    if (error) return <div className="page-container"><h2>{error}</h2></div>;

    const isSuspended = server?.status === 'suspended';
    const isLite = server?.isLite ?? true;
    const isExpiringSoon = server?.contractEndDate && (new Date(server.contractEndDate) - new Date()) / (1000 * 60 * 60 * 24) <= 90;

    // --- SUB-COMPONENTS ---
    const DashboardCard = ({ title, subtitle = "Manage & View", count, isOverLimit, icon, color, path, onAdd }) => {
        return (
            <div 
                style={{ 
                    backgroundColor: isOverLimit ? '#fff5f5' : 'white', 
                    borderRadius: '10px', padding: '20px', 
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)', cursor: 'pointer',
                    borderLeft: `5px solid ${isOverLimit ? '#e53e3e' : color}`,
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    transition: 'transform 0.2s', minHeight: '140px'
                }}
                onClick={() => navigate(path)}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h3 style={{ margin: '0 0 5px 0', color: isOverLimit ? '#9b2c2c' : '#2c3e50', fontSize: '16px' }}>{title}</h3>
                        <div style={{ fontSize: '13px', color: isOverLimit ? '#c53030' : '#718096' }}>
                            {isOverLimit ? '⚠️ Staff Limit Exceeded' : subtitle}
                        </div>
                    </div>
                    <div style={{ fontSize: '24px', opacity: 0.8 }}>{icon}</div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: '15px' }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: isOverLimit ? '#e53e3e' : '#2d3748' }}>
                        {count !== undefined ? count : '-'} 
                    </div>
                    {onAdd && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); onAdd(); }}
                            style={{ 
                                width: '32px', height: '32px', borderRadius: '50%', border: 'none', 
                                backgroundColor: isOverLimit ? '#fed7d7' : '#edf2f7', 
                                color: isOverLimit ? '#c53030' : color, 
                                fontSize: '18px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                            title={`Add New ${title}`}
                        >
                            +
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const WideCard = ({ title, subtitle, icon, color, path, state = {} }) => (
        <div 
            onClick={() => navigate(path, { state })}
            style={{ 
                backgroundColor: 'white', borderRadius: '10px', padding: '25px', 
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)', cursor: 'pointer',
                borderLeft: `5px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '20px'
            }}
        >
            <div>
                <h3 style={{ margin: '0 0 5px 0', color: '#2c3e50', fontSize: '18px' }}>{title}</h3>
                <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>{subtitle}</p>
            </div>
            <span style={{ fontSize: '28px' }}>{icon}</span>
        </div>
    );

    return (
        <div className="page-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {disableModalOpen && <DisableServerModal server={{ server_name: server.companyName }} isEnabling={isSuspended} closeModal={() => setDisableModalOpen(false)} onConfirm={handleConfirmToggle} />}
            {isEditModalOpen && <EditServerModal server={server} closeModal={() => setIsEditModalOpen(false)} onUpdate={fetchAllDetails} />}

            {server?.status === 'archived' && (
                <div style={{ backgroundColor: '#fed7d7', borderLeft: '5px solid #e53e3e', padding: '15px 20px', marginBottom: '20px', borderRadius: '4px', color: '#9b2c2c' }}>
                    <strong>⚠️ THIS SERVER IS ARCHIVED.</strong> All associated users are currently blocked from accessing the system. You must un-archive it from the Archived Servers page to make changes.
                </div>
            )}

            {/* HEADER */}
            <div style={{ marginBottom: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <Link to="/servers" style={{ textDecoration: 'none', color: '#718096', fontSize: '13px', fontWeight: '500' }}>← Back to Server List</Link>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px', flexWrap: 'wrap' }}>
                            <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '28px' }}>{server?.companyName}</h1>
                            
                            <span style={{ 
                                padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase',
                                backgroundColor: isLite ? '#edf2f7' : '#faf5ff',
                                color: isLite ? '#4a5568' : '#553c9a',
                                border: `1px solid ${isLite ? '#cbd5e0' : '#e9d8fd'}`
                            }}>
                                {isLite ? 'FREEMIUM' : 'PREMIUM'}
                            </span>

                            <span style={{ 
                                backgroundColor: server?.status === 'archived' ? '#edf2f7' : (isSuspended ? '#fff5f5' : '#f0fff4'), 
                                color: server?.status === 'archived' ? '#4a5568' : (isSuspended ? '#c53030' : '#276749'), 
                                border: `1px solid ${server?.status === 'archived' ? '#cbd5e0' : (isSuspended ? '#feb2b2' : '#9ae6b4')}`, 
                                padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase' 
                            }}>
                                {server?.status || 'Active'}
                            </span>

                            {/* Total Users Info Badge */}
                            <span style={{ 
                                backgroundColor: isGlobalOverLimit ? '#fed7d7' : '#e2e8f0', 
                                color: isGlobalOverLimit ? '#9b2c2c' : '#2d3748', 
                                border: `1px solid ${isGlobalOverLimit ? '#fc8181' : '#cbd5e0'}`,
                                padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' 
                            }}>
                                👥 Staff Usage: {totalActiveStaff} / {maxUsers > 0 ? maxUsers : '∞'}
                            </span>

                            {isExpiringSoon && (
                                <span style={{ 
                                    backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fc8181', 
                                    padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' 
                                }}>
                                    🚨 Expiring Soon
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '20px', marginTop: '10px', color: '#4a5568', fontSize: '14px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>📧 <a href={`mailto:${server?.companyEmail}`} style={{ color: 'inherit', textDecoration: 'none' }}>{server?.companyEmail}</a></span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>📞 <a href={`tel:${server?.phoneNumber}`} style={{ color: 'inherit', textDecoration: 'none' }}>{server?.phoneNumber}</a></span>
                            <span style={{ color: '#a0aec0' }}>|</span>
                            <span style={{ color: '#718096' }}>ID: {serverId}</span>
                        </div>
                    </div>
                    
                    {server?.status !== 'archived' && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {canEdit && (
                                <button onClick={() => setIsEditModalOpen(true)} style={{ padding: '10px 20px', backgroundColor: 'white', color: '#3182ce', border: '1px solid #3182ce', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>Edit Profile</button>
                            )}
                            {canDelete && (
                                <button onClick={() => setDisableModalOpen(true)} style={{ padding: '10px 20px', backgroundColor: isSuspended ? '#48bb78' : '#e53e3e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{isSuspended ? 'Enable Server' : 'Disable Server'}</button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* LAYOUT */}
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                <div style={{ flex: '2', minWidth: '600px' }}>
                    <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>Management Overview</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                        
                        <DashboardCard 
                            title="Admins" 
                            count={`${activeAdminsCount} / ${admins.length}`}
                            isOverLimit={isGlobalOverLimit} 
                            icon="🛡️" color="#3182ce" path={`/servers/${serverId}/admins`} 
                            onAdd={canEdit ? () => handleAddShortcut(`/servers/${serverId}/admins`) : undefined} 
                        />
                        
                        <DashboardCard 
                            title="Employees" 
                            count={`${activeEmployeesCount} / ${employees.length}`}
                            isOverLimit={isGlobalOverLimit}
                            icon="👔" color="#38a169" path={`/servers/${serverId}/employees`} 
                            onAdd={canEdit ? () => handleAddShortcut(`/servers/${serverId}/employees`) : undefined} 
                        />
                        
                        <DashboardCard 
                            title="Service Users" 
                            count={`${activeServiceUsersCount} / ${serviceUsers.length}`}
                            isOverLimit={false} 
                            icon="🏥" color="#dd6b20" path={`/servers/${serverId}/service-users`} 
                            onAdd={canEdit ? () => handleAddShortcut(`/servers/${serverId}/service-users`) : undefined} 
                        />
                        
                        {/* Left this as a single number since "Login History" applies to everyone ever created */}
                        <DashboardCard 
                            title="Users Login History" 
                            subtitle="Total System Users" 
                            count={admins.length + employees.length} 
                            isOverLimit={false} 
                            icon="🔐" color="#319795" path={`/servers/${serverId}/login-history`} 
                        />
                    </div>
                    
                    <div>
                        <WideCard title="Visit Management" subtitle="View full schedule, track missed visits, and manage logs." icon="📅" color="#6b46c1" path={`/servers/${serverId}/visits`} />
                        <WideCard title="Business Growth & Analytics" subtitle="View retention rates, acquisition velocity, and sales pipeline." icon="📈" color="#ed64a6" path={`/servers/${serverId}/business-development`} />
                    </div>

                    <h2 style={{ fontSize: '18px', color: '#4a5568', marginTop: '30px', marginBottom: '20px' }}>Clinical Management</h2>
                    <div>
                        <WideCard 
                            title="Medication Hub" 
                            subtitle="Manage prescriptions, track compliance, and view global medication statistics." 
                            icon="💊" 
                            color="#38a169" 
                            path={`/servers/${serverId}/medications`} 
                        />
                    </div>
                    <h2 style={{ fontSize: '18px', color: '#4a5568', marginTop: '30px', marginBottom: '20px' }}>Live Operations Center</h2>
                    <div>
                        <WideCard 
                            title="Broadcast Hub" 
                            subtitle="Send server-wide announcements to staff and administrators." 
                            icon="📢" 
                            color="#3182ce" 
                            path={`/servers/${serverId}/broadcasts`} 
                        />
                        <WideCard title="Emergency Alerts" subtitle="View critical SOS alerts requiring immediate attention." icon="🆘" color="#e53e3e" path={`/servers/${serverId}/emergency-alerts`} />
                        <WideCard title="Visit Alerts" subtitle="Monitor late visits, safety issues, and communications." icon="🚨" color="#d69e2e" path={`/servers/${serverId}/visit-alerts`} />
                        <WideCard title="General Notifications" subtitle="View system logs and general updates." icon="🔔" color="#718096" path={`/servers/${serverId}/notifications`} />
                    </div>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <h2 style={{ fontSize: '18px', color: '#4a5568', marginBottom: '20px' }}>Location & Status</h2>
                    <div style={{ backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
                        <div style={{ height: '250px', backgroundColor: '#edf2f7' }}>
                            {server?.companyAddress ? (
                                <iframe width="100%" height="100%" frameBorder="0" style={{ border: 0 }} src={`https://maps.google.com/maps?q=${encodeURIComponent(server?.companyAddress || '')}&t=&z=15&ie=UTF8&iwloc=&output=embed`} title="Server Location"></iframe>
                            ) : (<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a0aec0' }}>No Address Provided</div>)}
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#a0aec0', fontWeight: 'bold' }}>Head Office</div>
                            <div style={{ color: '#2d3748', fontWeight: '500', marginTop: '5px' }}>{server?.companyAddress || 'N/A'}</div>
                        </div>
                    </div>
                    <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '25px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}><span>⏰</span> Opening Hours</h3>
                        <div style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{server?.openingHours ? server.openingHours : "No hours configured."}</div>
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #edf2f7', fontSize: '14px', color: '#2d3748' }}>📅 Week Starts On: <strong>{server?.weekStartDay || 'Monday'}</strong></div>
                    </div>
                    <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '25px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#2c3e50' }}>Audit Log</h3>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}><span style={{ color: '#718096', fontSize: '14px' }}>Server Toggle Count</span><span style={{ fontWeight: 'bold', color: '#2d3748' }}>{server?.statusToggleCount || 0}</span></div>
                        <Link to={`/servers/${serverId}/status-history`} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'center', backgroundColor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '6px', textDecoration: 'none', color: '#4a5568', fontSize: '13px', fontWeight: '600' }}>View Full History Log</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerDetailsPage;